import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';

import { TextSearchService } from '@monsido/services/text-search/text-search.service';

import { SessionService } from '@monsido/core/session/session.service';
import {
    AccessibilityAutofixService,
} from '@client/app/modules/page-fix/services/accessibility-auto-fix/accessibility-auto-fix.service';
import {
    PageFixAccessibilityDifficultyService,
} from '@client/app/modules/page-fix/services/accessibility-difficulty/page-fix-accessibility-difficulty.service';
import {
    PageFixAccessibilityConfigService,
} from '@client/app/modules/page-fix/services/page-fix-accessibility-config/page-fix-accessibility-config.service';
import {
    PAGE_FIX_ACCESSIBILITY_CHECK_DIFFICULTY,
} from '@client/app/modules/page-fix/constants/page-fix-accessibility-check-difficulity';
import { IconsService } from '@layout/icons/icons.service';
import {
    PAGE_FIX_ACCESSIBILITY_CHECK_TAB,
} from '@client/app/modules/page-fix/constants/page-fix-accessibility-check-tab';
import { cloneDeep } from 'lodash';
import PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG from '@client/app/modules/page-fix/constants/page-fix-accessibility-check-config.json';

@Injectable({
    providedIn: 'root',
})
export class PageFixAccessibilityChecksService {
    private accessibilityCheckConfig = cloneDeep(PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG);

    constructor (
        private accessibilityAutofixService: AccessibilityAutofixService,
        private textSearchService: TextSearchService,
        private translateService: TranslateService,
        private monIcons: IconsService,
        private pageFixAccessibilityDifficultyService: PageFixAccessibilityDifficultyService,
        private pageFixAccessibilityConfigService: PageFixAccessibilityConfigService,
        private sessionService: SessionService,
    ) {}

    filterAccessibilityChecks (accessibilityChecks: any[], search?: string, tab?: string): any[] {
        return accessibilityChecks.filter(accessibilityCheck =>
            this.accessibilityAutofixService.useCheckId(accessibilityCheck.id) &&
      (!search || this.textSearchService.fuzzySearch(search, accessibilityCheck.nameEscaped)) &&
      (!tab || this.hasTab(accessibilityCheck, tab)),
        );
    }

    getAccessibilityCheckConfig () {
        return this.accessibilityCheckConfig;
    }

    sortAccessibilityChecks (checks: any[], pageFixChecks: any, sortBy?: string, sortDirection?: string) {
        if (!sortBy) {
            return checks;
        }
        return checks.sort((a, b) => {
            if (sortBy === 'status') {
                const pageFixA = Boolean(pageFixChecks[a.id]);
                const pageFixB = Boolean(pageFixChecks[b.id]);
                if (pageFixA !== pageFixB) {
                    if (sortDirection === 'asc') {
                        return pageFixA ? 1 : -1;
                    } else {
                        return pageFixA ? -1 : 1;
                    }
                }
            } else if (sortBy === 'difficulty') {
                const difficultyA = this.getDifficulty(a);
                const difficultyB = this.getDifficulty(b);
                if (difficultyA.weight !== difficultyB.weight) {
                    if (sortDirection === 'asc') {
                        return difficultyA.weight > difficultyB.weight ? 1 : -1;
                    } else {
                        return difficultyA.weight > difficultyB.weight ? -1 : 1;
                    }
                }
            } else {
                const lowercaseA = a.nameEscaped.toLowerCase();
                const lowercaseB = b.nameEscaped.toLowerCase();
                if (lowercaseA !== lowercaseB) {
                    if (sortDirection === 'asc') {
                        return lowercaseA > lowercaseB ? 1 : -1;
                    } else {
                        return lowercaseA > lowercaseB ? -1 : 1;
                    }
                }
            }
            return 0;
        });
    }

    getDifficulty (accessibilityCheck: any) {
        const config = PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG[accessibilityCheck.id];
        return this.pageFixAccessibilityDifficultyService.getDifficulty(config.difficulty);
    }

    isHardDifficulty (check: any) {
        const difficulty = this.getDifficulty(check);
        return PAGE_FIX_ACCESSIBILITY_CHECK_DIFFICULTY.HARD === difficulty.value;
    }

    getDescription (check: any) {
        return this.pageFixAccessibilityConfigService.getDescription(check);
    }

    getImage (accessibilityCheck: any) {
        const config = PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG[accessibilityCheck.id];
        return config.imagePath;
    }

    getLoadingScriptUrl () {
        if (this.sessionService.accessibilityMode) {
            return '/images/page-fix/accessibility-mode-script-loading.gif';
        }
        return '/images/page-fix/script-loading.gif';
    }

    getTabIcon (accessibilityCheck: any) {
        const config = PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG[accessibilityCheck.id];
        switch (config.work_types[0]) {
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.WEBSITE_STRUCTURE:
                return this.monIcons.getIcon(['PAGE_FIX', 'WEBSITE_STRUCTURE']);
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.DESIGN:
                return this.monIcons.getIcon(['PAGE_FIX', 'DESIGN']);
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.LINKS:
                return this.monIcons.getIcon(['PAGE_FIX', 'LINKS']);
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.MEDIA:
                return this.monIcons.getIcon(['PAGE_FIX', 'MEDIA']);
            default:
                return 'fas fa-question';
        }
    }

    getTabTranslation (accessibilityCheck: any) {
        const config = PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG[accessibilityCheck.id];
        switch (config.work_types[0]) {
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.WEBSITE_STRUCTURE:
                return this.translateService.getString('Website Structure');
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.DESIGN:
                return this.translateService.getString('Design');
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.LINKS:
                return this.translateService.getString('Links');
            case PAGE_FIX_ACCESSIBILITY_CHECK_TAB.MEDIA:
                return this.translateService.getString('Media');
            default:
                return this.translateService.getString('Unknown');
        }
    }

    private hasTab (accessibilityCheck: any, tab: string) {
        const config = PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG[accessibilityCheck.id];
        return config.work_types.indexOf(tab) > -1;
    }
}
